// ─── WTR Email Templates ──────────────────────────────────────────────────────
// Pure functions that return HTML strings.
// Colours match the WTR brand used in quoteExport.js.
// All templates are mobile-responsive single-column layouts.

const NAVY  = '#0F1F3D'
const GREEN = '#2A9D5C'
const LIGHT = '#F4F6FA'

function base(title, bodyHtml) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
  <style>
    body { margin: 0; padding: 0; background: #f0f2f5; font-family: Arial, Helvetica, sans-serif; }
    .wrap { max-width: 600px; margin: 32px auto; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,.10); }
    .header { background: ${NAVY}; padding: 28px 36px; }
    .header h1 { margin: 0; color: #ffffff; font-size: 22px; font-weight: 700; letter-spacing: .5px; }
    .header p  { margin: 4px 0 0; color: rgba(255,255,255,.65); font-size: 13px; }
    .body  { padding: 32px 36px; color: #1a1a2e; font-size: 15px; line-height: 1.65; }
    .body h2 { margin: 0 0 16px; font-size: 17px; color: ${NAVY}; }
    .info-box { background: ${LIGHT}; border-left: 4px solid ${GREEN}; border-radius: 4px; padding: 16px 20px; margin: 20px 0; font-size: 14px; }
    .info-box strong { display: block; color: ${NAVY}; margin-bottom: 4px; font-size: 13px; text-transform: uppercase; letter-spacing: .4px; }
    .btn-row { margin: 28px 0 8px; text-align: center; }
    .btn { display: inline-block; padding: 14px 32px; border-radius: 6px; font-size: 15px; font-weight: 700; text-decoration: none; letter-spacing: .3px; }
    .btn-yes  { background: ${GREEN}; color: #ffffff; margin-right: 12px; }
    .btn-no   { background: #e74c3c; color: #ffffff; }
    .btn-accept  { background: ${GREEN}; color: #ffffff; margin-right: 12px; }
    .btn-decline { background: #6b7280; color: #ffffff; }
    .btn-single  { background: ${NAVY}; color: #ffffff; }
    .footer { background: ${LIGHT}; padding: 18px 36px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e2e8f0; }
    .divider { border: none; border-top: 1px solid #e2e8f0; margin: 24px 0; }
    @media (max-width: 600px) {
      .wrap { margin: 0; border-radius: 0; }
      .body, .header, .footer { padding-left: 20px; padding-right: 20px; }
      .btn { display: block; margin: 8px auto; max-width: 220px; text-align: center; }
      .btn-yes { margin-right: 0; }
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="header">
      <h1>Winnipeg Tent Rental</h1>
      <p>Professional Tent &amp; Event Equipment</p>
    </div>
    <div class="body">
      ${bodyHtml}
    </div>
    <div class="footer">
      Winnipeg Tent Rental &nbsp;·&nbsp; Winnipeg, MB &nbsp;·&nbsp; info@winnipegtentrentals.ca<br/>
      This email was sent automatically — please do not reply directly.
    </div>
  </div>
</body>
</html>`
}

// ─── Staff Availability Ping ──────────────────────────────────────────────────
// Sent to a crew member asking if they're available for a job.
// yesUrl / noUrl point to /staff-respond/:token/yes|no

export function availabilityPingHtml({ staffName, jobDate, setupDate, teardownDate, eventType, address, crewRequired, setupHours, teardownHours, yesUrl, noUrl }) {
  const dateStr = jobDate ? new Date(jobDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD'
  const setupStr = setupDate ? new Date(setupDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : dateStr
  const teardownStr = teardownDate ? new Date(teardownDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : dateStr
  const totalHrs = (parseFloat(setupHours) || 0) + (parseFloat(teardownHours) || 0)

  return base('Are you available? — Winnipeg Tent Rental', `
    <h2>Are you available for this job?</h2>
    <p>Hi ${staffName || 'there'},</p>
    <p>We're building the crew for an upcoming job and wanted to check your availability. Please respond below as soon as possible.</p>
    <div class="info-box">
      <strong>Event Date</strong>${dateStr}
      ${setupDate && setupDate !== jobDate ? `<strong style="margin-top:10px">Setup Date</strong>${setupStr}` : ''}
      ${teardownDate && teardownDate !== jobDate ? `<strong style="margin-top:10px">Teardown Date</strong>${teardownStr}` : ''}
      ${eventType ? `<strong style="margin-top:10px">Event Type</strong>${eventType.charAt(0).toUpperCase() + eventType.slice(1)}` : ''}
      ${address ? `<strong style="margin-top:10px">Location</strong>${address}` : ''}
      ${totalHrs > 0 ? `<strong style="margin-top:10px">Estimated Hours</strong>~${totalHrs} hrs (${crewRequired || 2}-person crew)` : ''}
    </div>
    <div class="btn-row">
      <a href="${yesUrl}" class="btn btn-yes">✓ &nbsp;Yes, I'm Available</a>
      <a href="${noUrl}" class="btn btn-no">✗ &nbsp;Not Available</a>
    </div>
    <hr class="divider" />
    <p style="font-size:13px;color:#6b7280;">
      Tapping a button will record your response immediately. You can change your response by contacting the office directly.
    </p>
  `)
}

// ─── Crew Confirmation ────────────────────────────────────────────────────────
// Sent when admin confirms a staff member for a job.

export function crewConfirmationHtml({ staffName, jobDate, setupDate, teardownDate, eventType, address, setupHours, teardownHours, crewRequired, jobNotes }) {
  const dateStr = jobDate ? new Date(jobDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD'
  const setupStr = setupDate ? new Date(setupDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : null
  const teardownStr = teardownDate ? new Date(teardownDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : null
  const totalHrs = (parseFloat(setupHours) || 0) + (parseFloat(teardownHours) || 0)

  return base("You're confirmed! — Winnipeg Tent Rental", `
    <h2>You're confirmed for this job!</h2>
    <p>Hi ${staffName || 'there'},</p>
    <p>Great news — you've been confirmed as part of the crew for the following job. Please review the details below and be ready on time.</p>
    <div class="info-box">
      <strong>Event Date</strong>${dateStr}
      ${setupStr && setupStr !== dateStr ? `<strong style="margin-top:10px">Setup Date</strong>${setupStr}` : ''}
      ${teardownStr && teardownStr !== dateStr ? `<strong style="margin-top:10px">Teardown Date</strong>${teardownStr}` : ''}
      ${eventType ? `<strong style="margin-top:10px">Event Type</strong>${eventType.charAt(0).toUpperCase() + eventType.slice(1)}` : ''}
      ${address ? `<strong style="margin-top:10px">Location</strong>${address}` : ''}
      ${totalHrs > 0 ? `<strong style="margin-top:10px">Estimated Hours</strong>~${totalHrs} hrs (${crewRequired || 2}-person crew)` : ''}
      ${jobNotes ? `<strong style="margin-top:10px">Job Notes</strong>${jobNotes}` : ''}
    </div>
    ${jobNotes ? '' : '<p>Further details and any special instructions will be shared by the office closer to the date.</p>'}
    <p>If you have any questions or need to make changes, please contact us at <a href="mailto:info@winnipegtentrentals.ca" style="color:${GREEN}">info@winnipegtentrentals.ca</a>.</p>
  `)
}

// ─── Day-Before Reminder ──────────────────────────────────────────────────────
// Sent automatically the day before the event.

export function dayBeforeReminderHtml({ staffName, jobDate, setupDate, eventType, address, setupHours, teardownHours, crewRequired, jobNotes }) {
  const tomorrowStr = jobDate ? new Date(jobDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'tomorrow'
  const setupStr = setupDate ? new Date(setupDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : tomorrowStr

  return base('Reminder: Job Tomorrow — Winnipeg Tent Rental', `
    <h2>Reminder: You have a job tomorrow</h2>
    <p>Hi ${staffName || 'there'},</p>
    <p>This is a friendly reminder that you're scheduled to work <strong>${eventType ? `a ${eventType} ` : ''}job tomorrow</strong>. Here are the details:</p>
    <div class="info-box">
      <strong>Date</strong>${setupStr !== tomorrowStr ? `Setup: ${setupStr}<br/>Event: ${tomorrowStr}` : tomorrowStr}
      ${eventType ? `<strong style="margin-top:10px">Event Type</strong>${eventType.charAt(0).toUpperCase() + eventType.slice(1)}` : ''}
      ${address ? `<strong style="margin-top:10px">Location</strong>${address}` : ''}
      ${setupHours > 0 ? `<strong style="margin-top:10px">Setup Hours</strong>~${setupHours} hrs` : ''}
      ${teardownHours > 0 ? `<strong style="margin-top:10px">Teardown Hours</strong>~${teardownHours} hrs` : ''}
      ${crewRequired ? `<strong style="margin-top:10px">Crew Size</strong>${crewRequired} people` : ''}
      ${jobNotes ? `<strong style="margin-top:10px">Notes</strong>${jobNotes}` : ''}
    </div>
    <p>Please arrive on time and come prepared. If there's an emergency and you can't make it, contact the office immediately at <a href="mailto:info@winnipegtentrentals.ca" style="color:${GREEN}">info@winnipegtentrentals.ca</a>.</p>
    <p style="font-weight:700;color:${NAVY}">See you tomorrow!</p>
  `)
}

// ─── Quote Acceptance Link ────────────────────────────────────────────────────
// Sent to a client with a link to accept or decline their quote.
// acceptUrl / declineUrl point to /quote-accept/:token?response=accept|decline

export function quoteAcceptanceHtml({ clientName, quoteNumber, eventDate, eventType, address, grandTotal, depositRequired, acceptUrl, declineUrl }) {
  const dateStr = eventDate ? new Date(eventDate + 'T12:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD'
  const fmt = n => parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return base(`Your Quote ${quoteNumber} — Winnipeg Tent Rental`, `
    <h2>Your quote is ready to review</h2>
    <p>Hi ${clientName || 'there'},</p>
    <p>Thank you for choosing Winnipeg Tent Rental! Please review your quote below and let us know if you'd like to move forward.</p>
    <div class="info-box">
      <strong>Quote Number</strong>${quoteNumber}
      <strong style="margin-top:10px">Event Date</strong>${dateStr}
      ${eventType ? `<strong style="margin-top:10px">Event Type</strong>${eventType.charAt(0).toUpperCase() + eventType.slice(1)}` : ''}
      ${address ? `<strong style="margin-top:10px">Location</strong>${address}` : ''}
      <strong style="margin-top:12px;font-size:15px">Quote Total</strong>
      <span style="font-size:20px;font-weight:700;color:${GREEN}">$${fmt(grandTotal)}</span>
      ${depositRequired > 0 ? `<strong style="margin-top:8px">Deposit Required</strong>$${fmt(depositRequired)} to secure your date` : ''}
    </div>
    <p>To accept this quote and reserve your date, click <strong>Accept Quote</strong> below. If you'd like to decline, click <strong>Decline</strong>.</p>
    <div class="btn-row">
      <a href="${acceptUrl}" class="btn btn-accept">✓ &nbsp;Accept Quote</a>
      <a href="${declineUrl}" class="btn btn-decline">Decline</a>
    </div>
    <hr class="divider" />
    <p style="font-size:13px;color:#6b7280;">
      This link is unique to your quote. If you have any questions about the quote or would like to make changes, please reply to this email or call us directly.
    </p>
  `)
}
